<div class="flex flex-col px-6 pt-6 gap-3">
  <div class="flex items-center justify-between">
    <h1 class="ml-10 headline-small text-primary flex-1 text-center" data-testid="select-visit-title">{{ 'Select a visit' | translate }}</h1>
    <button mat-icon-button
      (click)="close()"
      [attr.aria-label]="'Close' | translate"
      data-testid="select-visit-close-button">
      <mat-icon class="text-on-surface-variant">close</mat-icon>
    </button>
  </div>
</div>

<div class="px-6 pt-6">
  @if (showLoadingSkeleton) {
    <app-encounter-skeleton [count]="3" [showHeader]="true" [showWrapper]="true"></app-encounter-skeleton>
  } @else if (isLoading) {
    <div class="h-[224px]"></div>
  } @else if (hasVisits) {
    <div class="select-visit-table table-wrapper bg-surface-bright border border-surface-varient rounded-lg w-full max-h-[420px] overflow-auto">
      <table mat-table [dataSource]="visits" class="min-w-[1000px] w-full">

        <ng-container matColumnDef="select">
          <th mat-header-cell *matHeaderCellDef class="w-11"></th>
          <td mat-cell *matCellDef="let visit">
            <!-- Pointer events belong to the visit row, so the radio can never race it; keyboard still reports through (change). -->
            <mat-radio-button class="pointer-events-none" name="select-visit" [checked]="isSelected(visit)"
              (change)="onSelectVisit(visit)"
              [attr.aria-label]="visit.location || visit.visitNumber"></mat-radio-button>
          </td>
        </ng-container>

        <ng-container matColumnDef="location">
          <th mat-header-cell *matHeaderCellDef>{{ 'Location' | translate }}</th>
          <td mat-cell *matCellDef="let visit" [title]="visit.location">{{ visit.location || '—' }}</td>
        </ng-container>

        <ng-container matColumnDef="tasks">
          <th mat-header-cell *matHeaderCellDef>{{ 'Tasks' | translate }}</th>
          <td mat-cell *matCellDef="let visit">
            @if (visit.hasTasks) {
              <mat-icon class="!h-6 !w-6 !text-2xl text-on-surface material-symbols-outlined"
                [attr.aria-label]="'Already documented' | translate">check_circle</mat-icon>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="consultant">
          <th mat-header-cell *matHeaderCellDef>{{ 'Consultant' | translate }}</th>
          <td mat-cell *matCellDef="let visit" class="max-w-[160px] truncate" [title]="visit.consultant">{{ visit.consultant || '—' }}</td>
        </ng-container>

        <ng-container matColumnDef="referrer">
          <th mat-header-cell *matHeaderCellDef>{{ 'Referrer' | translate }}</th>
          <td mat-cell *matCellDef="let visit" class="max-w-[160px] truncate" [title]="visit.referrer">{{ visit.referrer || '—' }}</td>
        </ng-container>

        <ng-container matColumnDef="admit">
          <th mat-header-cell *matHeaderCellDef>{{ 'Admit' | translate }}</th>
          <td mat-cell *matCellDef="let visit" class="whitespace-nowrap">{{ (visit.admitDateTime | date:'dd/MM/yyyy HH:mm') || '—' }}</td>
        </ng-container>

        <ng-container matColumnDef="discharge">
          <th mat-header-cell *matHeaderCellDef>{{ 'Discharge' | translate }}</th>
          <td mat-cell *matCellDef="let visit" class="whitespace-nowrap">{{ (visit.dischargeDateTime | date:'dd/MM/yyyy HH:mm') || '—' }}</td>
        </ng-container>

        <ng-container matColumnDef="visitNumber">
          <th mat-header-cell *matHeaderCellDef>{{ 'Visit #' | translate }}</th>
          <td mat-cell *matCellDef="let visit">{{ visit.visitNumber || '—' }}</td>
        </ng-container>

        <ng-container matColumnDef="clinicCode">
          <th mat-header-cell *matHeaderCellDef>{{ 'Clinic code' | translate }}</th>
          <td mat-cell *matCellDef="let visit" [title]="visit.clinicCode">{{ visit.clinicCode || '—' }}</td>
        </ng-container>

        <ng-container matColumnDef="type">
          <th mat-header-cell *matHeaderCellDef>{{ 'Type' | translate }}</th>
          <td mat-cell *matCellDef="let visit">{{ visit.type || '—' }}</td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
        <tr mat-row *matRowDef="let visit; columns: displayedColumns;"
          class="cursor-pointer"
          (click)="onVisitClick(visit)"
          (keydown)="onVisitKeydown(visit, $event)"
          data-testid="select-visit-row"></tr>
      </table>
    </div>
  } @else {
    <div class="flex flex-col items-center justify-center h-[224px]" data-testid="select-visit-empty-state">
      <img src="assets/folder_empty.svg" alt="" class="h-20 w-20 mb-4 opacity-70" />
      <p class="title-medium text-primary">{{ 'No visits available' | translate }}</p>
      <p class="body-medium text-on-surface-variant mt-1">{{ emptyStateHintKey | translate }}</p>
    </div>
  }
</div>

<div class="px-6 pb-6">
  <div class="flex flex-col-reverse md:flex-row items-center gap-2 md:gap-3 pt-6 w-full">
    <button mat-button class="title-medium text-primary max-md:w-full" (click)="goBack()" data-testid="select-visit-back-button">
      <mat-icon>chevron_left</mat-icon>
      {{ 'Back' | translate }}
    </button>

    @if (hasVisits) {
      <button mat-stroked-button color="primary" class="h-12 rounded-[8px] ml-auto max-md:w-full" [disabled]="isBusy"
        (click)="startWithoutVisit()"
        data-testid="select-visit-without-button">{{ withoutVisitLabelKey | translate }}</button>
      <button mat-flat-button color="primary" class="h-12 rounded-[8px] max-md:w-full" [disabled]="isBusy || !selectedVisit"
        (click)="startWithVisit()"
        data-testid="select-visit-start-button">{{ primaryActionLabelKey | translate }}</button>
    } @else {
      <button mat-flat-button color="primary" class="h-12 rounded-[8px] ml-auto max-md:w-full" [disabled]="isBusy"
        (click)="startWithoutVisit()"
        data-testid="select-visit-start-empty-button">{{ emptyActionLabelKey | translate }}</button>
    }
  </div>
</div>
